import React, { useState } from 'react';
import { Building2, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, Target, Scale, FileText, Users, ChevronDown, DollarSign } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';

const LLCFormationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Limited Liability',
      description: 'Protect personal assets from business debts and lawsuits'
    },
    {
      icon: DollarSign,
      title: 'Tax Flexibility',
      description: 'Choose to be taxed as sole prop, partnership, S-corp, or C-corp'
    },
    {
      icon: CheckCircle,
      title: 'Simple Management',
      description: 'Fewer formalities and reporting requirements than corporations'
    },
    {
      icon: TrendingUp,
      title: 'Business Credibility',
      description: 'Enhance professional image and gain customer trust'
    }
  ];

  const packages = [
    {
      name: 'Basic LLC',
      price: '$299',
      description: 'Essential formation documents and state filing',
      features: [
        'LLC formation documents',
        'Articles of Organization filing',
        'State filing fees included',
        'Basic Operating Agreement',
        'Email delivery',
        '5-7 business days'
      ]
    },
    {
      name: 'Standard LLC',
      price: '$499',
      description: 'Complete formation with EIN and banking support',
      features: [
        'All Basic features',
        'Federal EIN from IRS',
        'Banking resolution',
        'Comprehensive Operating Agreement',
        'Registered agent (1 year)',
        'Priority processing (3-5 days)'
      ],
      recommended: true
    },
    {
      name: 'Premium LLC',
      price: '$799',
      description: 'Full-service formation with compliance tools',
      features: [
        'All Standard features',
        'Lifetime company alerts',
        'Business license research',
        'Custom operating agreement',
        'Compliance calendar',
        'Expedited processing (1-3 days)',
        'Business banking consultation'
      ]
    }
  ];

  const stateOptions = [
    {
      state: 'Delaware',
      benefits: 'Business-friendly laws, privacy protection, no sales tax',
      bestFor: 'Startups seeking investors, businesses planning to go public',
      cost: '$90 state filing fee'
    },
    {
      state: 'Wyoming',
      benefits: 'No state income tax, low fees, strong asset protection',
      bestFor: 'Asset protection, real estate holdings, online businesses',
      cost: '$100 state filing fee'
    },
    {
      state: 'Nevada',
      benefits: 'No corporate or personal income tax, privacy protection',
      bestFor: 'Privacy-focused businesses, high-income owners',
      cost: '$75 state filing fee'
    },
    {
      state: 'Your Home State',
      benefits: 'Simplest option, local presence, no foreign LLC fees',
      bestFor: 'Local businesses, physical locations, most small businesses',
      cost: 'Varies by state ($50-$500)'
    }
  ];

  const timeline = [
    { time: 'Day 1', activity: 'Complete LLC formation questionnaire and select your business name' },
    { time: 'Day 2', activity: 'Review and approve Articles of Organization and Operating Agreement' },
    { time: 'Day 3-5', activity: 'File with state and obtain approval (varies by state processing time)' },
    { time: 'Day 6-7', activity: 'Receive EIN from IRS, complete formation documents, and LLC kit' }
  ];

  const faqs = [
    {
      question: 'What is an LLC and how does it work?',
      answer: 'A Limited Liability Company (LLC) is a business structure that combines the liability protection of a corporation with the tax benefits and simplicity of a sole proprietorship or partnership. LLC owners (called members) are not personally liable for business debts and lawsuits. The LLC is a separate legal entity that can own property, enter contracts, and conduct business. LLCs offer flexible management structures and can be managed by members or appointed managers.'
    },
    {
      question: 'How is an LLC different from a sole proprietorship or corporation?',
      answer: 'Sole Proprietorship: No liability protection, simplest structure, owner and business are legally the same. LLC: Limited liability protection, separate legal entity, flexible tax options, minimal formalities. Corporation: Strongest liability protection, double taxation (C-corp) or pass-through (S-corp), strict formalities and record-keeping. LLCs offer the best balance of protection, simplicity, and tax flexibility for most small businesses.'
    },
    {
      question: 'Which state should I form my LLC in?',
      answer: 'Most small businesses should form in their home state where they physically operate. You\'ll need to register as a foreign LLC in any state where you do business anyway, doubling your costs. However, Delaware is popular for startups seeking venture capital due to well-established business laws. Wyoming and Nevada offer tax advantages and privacy protection. Consider: where you do business physically, state tax rates, filing fees, annual report requirements, and whether you\'ll seek outside investment.'
    },
    {
      question: 'How much does it cost to form and maintain an LLC?',
      answer: 'Formation costs: State filing fees: $50-$500 (varies by state), Operating Agreement: $0-$500 (included in our packages), EIN (free from IRS), Registered Agent: $100-$300/year. Annual costs: Annual report/franchise tax: $0-$800 (varies by state), Registered agent: $100-$300/year, Business licenses: varies by industry and location. Total first year: typically $500-$1,500 including our formation service. Annual maintenance: $100-$1,000 depending on your state.'
    },
    {
      question: 'Do I need an Operating Agreement?',
      answer: 'While not required in most states, an Operating Agreement is highly recommended for all LLCs, even single-member LLCs. It: establishes ownership percentages and profit distribution, defines member rights and responsibilities, provides management structure and voting rules, protects limited liability status, prevents state default rules from applying, and is often required by banks to open business accounts. Without an Operating Agreement, state law determines how your LLC operates, which may not match your intentions.'
    },
    {
      question: 'How are LLCs taxed?',
      answer: 'LLCs are "pass-through" entities by default, meaning profits and losses pass through to owners\' personal tax returns. Single-member LLCs are taxed as sole proprietorships, multi-member LLCs as partnerships. However, LLCs can elect to be taxed as S-corporations or C-corporations by filing with the IRS. S-corp election can save on self-employment taxes if the LLC is profitable. C-corp taxation subjects profits to corporate tax plus personal tax on dividends (double taxation). Most small LLCs benefit from default pass-through taxation or S-corp election.'
    }
  ];

  const requiredSteps = [
    {
      step: 'Choose Business Name',
      description: 'Select unique name that complies with state LLC naming rules',
      tips: ['Must include "LLC" or "Limited Liability Company"', 'Cannot be too similar to existing businesses', 'Check trademark availability']
    },
    {
      step: 'Appoint Registered Agent',
      description: 'Designate person/company to receive legal documents',
      tips: ['Must have physical address in formation state', 'Available during business hours', 'We provide registered agent service']
    },
    {
      step: 'File Articles of Organization',
      description: 'Submit formation documents to state',
      tips: ['Includes LLC name, address, members/managers', 'State filing fees apply', 'Processing time varies by state']
    },
    {
      step: 'Create Operating Agreement',
      description: 'Define ownership, management, and operating procedures',
      tips: ['Not required but highly recommended', 'Prevents disputes among members', 'Required by most banks']
    },
    {
      step: 'Obtain EIN',
      description: 'Get federal Employer Identification Number from IRS',
      tips: ['Free from IRS', 'Required to open business bank account', 'Needed to hire employees']
    },
    {
      step: 'Comply with Requirements',
      description: 'Meet ongoing state and local compliance obligations',
      tips: ['Annual reports', 'Business licenses', 'State tax registrations']
    }
  ];

  const caseStudies = [
    {
      title: 'E-commerce Business Avoids Personal Liability',
      scenario: 'Online retailer faced product liability lawsuit from customer injury.',
      problem: 'Operating as sole proprietorship put founder\'s personal assets at risk.',
      solution: 'Formed LLC before lawsuit progressed, properly separated business and personal finances.',
      outcome: 'LLC absorbed lawsuit. Founder\'s personal home and savings protected. Business insurance covered settlement.'
    },
    {
      title: 'Consulting Firm Saves $12K in Taxes with S-Corp Election',
      scenario: 'Profitable consulting LLC with $180K net income paying high self-employment taxes.',
      problem: 'Paying 15.3% self-employment tax on all profits ($27,540 annually).',
      solution: 'Elected S-corp taxation, paid reasonable salary of $80K, took $100K as distributions.',
      outcome: 'Saved $15,300 in self-employment taxes annually. Paid only on salary portion, not distributions.'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              LLC Formation Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Form Your <span className="text-[#168356]">LLC</span> Today
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Protect your personal assets and simplify taxes with LLC formation. We handle all paperwork, state filings, and compliance requirements so you can focus on building your business.
            </p>
            <div className="flex gap-4">
              <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Start Your LLC Now
              </button>
              <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                Compare Packages
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#168356]" />
                Personal Asset Protection
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#168356]" />
                5-7 Day Formation
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#168356]" />
                100% Satisfaction Guaranteed
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">72%</div>
              <div className="text-gray-600 text-sm">Of new businesses choose LLC structure</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">$0</div>
              <div className="text-gray-600 text-sm">Personal liability for business debts</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">5-7days</div>
              <div className="text-gray-600 text-sm">Average formation time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">50+</div>
              <div className="text-gray-600 text-sm">States we file in</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is an LLC?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">The Smart Choice for Small Business Owners</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A Limited Liability Company (LLC) is the most popular business structure in America for good reason. It combines the liability protection of a corporation with the simplicity and tax benefits of a sole proprietorship or partnership.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                As an LLC owner, your personal assets (home, car, savings) are protected from business debts and lawsuits. The LLC is a separate legal entity, so creditors can only pursue business assets, not your personal wealth.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                LLCs offer incredible tax flexibility. By default, profits "pass through" to your personal tax return, avoiding double taxation. But you can also elect S-corporation or C-corporation tax treatment if it saves you money.
              </p>
              <p className="text-lg font-semibold text-black">
                Whether you're a freelancer, consultant, online business, or brick-and-mortar store, an LLC provides essential protection and credibility without the complexity of a corporation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-black mb-6">LLC Advantages:</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#168356] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-black">Limited Personal Liability</div>
                    <div className="text-gray-600 text-sm">Protects your personal assets from business debts and lawsuits</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#168356] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-black">Pass-Through Taxation</div>
                    <div className="text-gray-600 text-sm">Avoid double taxation; profits taxed only once on personal returns</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#168356] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-black">Flexible Management</div>
                    <div className="text-gray-600 text-sm">Member-managed or manager-managed, your choice</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#168356] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-black">Fewer Formalities</div>
                    <div className="text-gray-600 text-sm">No board meetings, minutes, or complex record-keeping required</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#168356] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-black">Enhanced Credibility</div>
                    <div className="text-gray-600 text-sm">Professional image builds trust with customers and partners</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">LLC Formation Packages</h2>
            <p className="text-lg text-gray-600">Choose the package that fits your needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${pkg.recommended ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {pkg.recommended && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{pkg.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{pkg.price}<span className="text-lg text-gray-500"> + state fees</span></div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${pkg.recommended ? 'bg-[#d29b00] text-white hover:bg-[#a87b00]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* State Selection */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Where Should You Form Your LLC?</h2>
            <p className="text-lg text-gray-600">Different states offer different advantages</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stateOptions.map((option, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-2xl font-bold text-black mb-3">{option.state}</h3>
                <p className="text-gray-700 mb-3">{option.benefits}</p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-900">Best for:</span>
                    <p className="text-gray-600">{option.bestFor}</p>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-[#168356] font-bold">{option.cost}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Formation Steps */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">LLC Formation Steps</h2>
            <p className="text-lg text-gray-600">What's involved in forming your LLC</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requiredSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{step.step}</h3>
                <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                <ul className="space-y-1">
                  {step.tips.map((tip, idx) => (
                    <li key={idx} className="text-xs text-gray-500 flex items-start gap-2">
                      <span className="text-[#168356]">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">LLC Formation Timeline</h2>
            <p className="text-lg text-gray-600">From start to finish in 5-7 business days</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Real-World LLC Success Stories</h2>
            <p className="text-lg text-gray-600">How LLCs protected these business owners</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#168356] uppercase mb-2">Solution</div>
                    <p className="text-gray-700">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of Our LLC Service</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default LLCFormationPage;